<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from "vue";
import { useThrottleFn } from "@vueuse/core";
import SlideDirectionIndicator from "@/Components/SlideDirectionIndicator.vue";

interface SlideOptions {
  moveThreshold: number;
  slideThreshold: number;
  maxSlideDistance: number;
  transitionDuration: number;
  disabled: boolean;
  resistance: number;
}

interface StyleOptions {
  transform?: string;
  transition?: string;
}

interface Props {
  classes?: string;
  options?: Partial<SlideOptions>;
  styleOptions?: StyleOptions;
}

const DEFAULT_OPTIONS: SlideOptions = {
  moveThreshold: 30,
  slideThreshold: 70,
  maxSlideDistance: 100,
  transitionDuration: 300,
  disabled: false,
  resistance: 0.5
} as const;

const props = withDefaults(defineProps<Props>(), {
  classes: "relative z-1",
  options: () => ({}),
  styleOptions: () => ({})
});

const mergedOptions = computed(() => ({
  ...DEFAULT_OPTIONS,
  ...props.options
}));

type SlideDirection = "left" | "right" | null;

const emit = defineEmits<{
  (e: "slide", direction: "left" | "right"): void;
  (e: "slideStart"): void;
  (e: "slideMove", position: number): void;
  (e: "slideEnd"): void;
}>();

// Reactive state
const state = ref({
  slidePosition: 0,
  isDragging: false,
  startX: 0,
  startY: 0,
  isHorizontalMove: false,
  slideDirection: null as SlideDirection,
  rafId: null as number | null
});

// Touch handling with performance optimizations
const calculateResistance = (distance: number): number => {
  const { maxSlideDistance, resistance } = mergedOptions.value;
  const resistedDistance = distance * resistance;
  return Math.max(Math.min(resistedDistance, maxSlideDistance), -maxSlideDistance);
};

const updateSlidePosition = (newPosition: number) => {
  if (state.value.rafId) {
    cancelAnimationFrame(state.value.rafId);
  }

  state.value.rafId = requestAnimationFrame(() => {
    state.value.slidePosition = newPosition;
    const direction = newPosition > 0 ? "left" : "right";
    state.value.slideDirection = direction;
    emit("slideMove", newPosition);
  });
};

const handleTouchStart = (e: TouchEvent) => {
  if (mergedOptions.value.disabled) return;

  state.value.isDragging = true;
  state.value.startX = e.touches[0].clientX;
  state.value.startY = e.touches[0].clientY;
  state.value.isHorizontalMove = false;

  emit("slideStart");
};

const handleTouchMove = useThrottleFn((e: TouchEvent) => {
  if (!state.value.isDragging || mergedOptions.value.disabled) return;

  const currentX = e.touches[0].clientX;
  const currentY = e.touches[0].clientY;
  const diffX = currentX - state.value.startX;
  const diffY = currentY - state.value.startY;

  if (!state.value.isHorizontalMove && Math.abs(diffX) >= mergedOptions.value.moveThreshold) {
    if (Math.abs(diffX) > Math.abs(diffY)) {
      state.value.isHorizontalMove = true;
      state.value.startX = currentX - state.value.slidePosition;
      e.preventDefault();
    }
  }

  if (state.value.isHorizontalMove) {
    e.preventDefault();
    e.stopPropagation();
    
    const rawPosition = currentX - state.value.startX;
    const resistedPosition = calculateResistance(rawPosition);
    updateSlidePosition(resistedPosition);
  }
}, 16); // ~60fps throttle

const handleTouchEnd = (e: TouchEvent) => {
  if (!state.value.isDragging || mergedOptions.value.disabled) return;

  if (state.value.isHorizontalMove) {
    e.preventDefault();

    const absPosition = Math.abs(state.value.slidePosition);
    if (absPosition >= mergedOptions.value.slideThreshold && 
        absPosition <= mergedOptions.value.maxSlideDistance) {
      const direction = state.value.slidePosition > 0 ? "right" : "left";
      emit("slide", direction);
    }
  }

  // Reset state
  state.value.isDragging = false;
  state.value.slidePosition = 0;
  state.value.isHorizontalMove = false;
  state.value.slideDirection = null;
  
  if (state.value.rafId) {
    cancelAnimationFrame(state.value.rafId);
    state.value.rafId = null;
  }

  emit("slideEnd");
};

// Cleanup
onUnmounted(() => {
  if (state.value.rafId) {
    cancelAnimationFrame(state.value.rafId);
  }
});

const containerStyle = computed(() => ({
  position: "relative",
  transform: state.value.slidePosition !== 0 ? `translateX(${state.value.slidePosition}px)` : "none",
  transition: state.value.isDragging ? "none" : `transform ${mergedOptions.value.transitionDuration}ms cubic-bezier(0.4, 0, 0.2, 1)`,
  willChange: state.value.isDragging ? "transform" : "auto",
  touchAction: "pan-y pinch-zoom",
  ...props.styleOptions
}));
</script>

<template>
  <div class="relative">
    <div 
      :class="classes" 
      :style="containerStyle" 
      @touchstart.passive="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
    >
      <slot />
    </div>
    <SlideDirectionIndicator 
      direction="left" 
      :visible="state?.slideDirection === 'left'" 
    />
    <SlideDirectionIndicator 
      direction="right" 
      :visible="state?.slideDirection === 'right'" 
    />
  </div>
</template>
